import { FeatureType, ImageUpload, SettingStructure, SettingsChangeEvent } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { StateController } from '../../helpers/state/state-management/state-controller';
import { TextArea } from '@material/mwc-textarea';
import featuresInfo from '../../helpers/state/features-info';
import { isDefined } from '../../helpers/utils';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-style-settings-fragment')
export class AdminStyleSettingsFragment extends LitElement {
    amplitude_callsite: string = 'Style Settings Drawer';

    @property({ type: Boolean })
    open: boolean = false;

    @query('#customMapGeoJson')
    private readonly customMapGeoJsonElement!: TextArea;

    @state()
    imageToUpload?: ImageUpload;

    pendingUpdates: { [key: string]: any } = {};

    initialMapStyleName: string = 'standard';

    initialHaloColor: string = '';

    fontField: SettingStructure = {
            fieldName: 'custom_font',
            title: 'Custom Font',
            inputType: 'dropdown',
            fieldType:'string',
            initialValueString: topicData.topic?.custom_font ?? 'Montserrat',
            description: 'Select a custom font for your map. Reach out to Proxi if you would like to request that we add your font.',
            featureType: FeatureType.CustomFont,
            hasEntitlement: featuresInfo.customFontEnabled,
            dropdownOptions: [
                { value: 'Amatic SC', label: 'Amatic SC' },
                { value: 'Bangers', label: 'Bangers' },
                { value: 'Bebas Neue', label: 'Bebas Neue' },
                { value: 'Cinzel', label: 'Cinzel' },
                { value: 'Cormorant Garamond', label: 'Cormorant Garamond' },
                { value: 'Courier Prime', label: 'Courier Prime' },
                { value: 'Creepster', label: 'Creepster' },
                { value: 'Dancing Script', label: 'Dancing Script' },
                { value: 'Gelasio', label: 'Gelasio' },
                { value: 'Great Vibes', label: 'Great Vibes' },
                { value: 'Homemade Apple', label: 'Homemade Apple' },
                { value: 'Jost', label: 'Jost' },
                { value: 'Lexend', label: 'Lexend' },
                { value: 'Lexend Deca', label: 'Lexend Deca' },
                { value: 'Montserrat', label: 'Montserrat' },
                { value: 'Noto Sans', label: 'Noto Sans' },
                { value: 'Orbitron', label: 'Orbitron' },
                { value: 'Oswald', label: 'Oswald' },
                { value: 'Pacifico', label: 'Pacifico' },
                { value: 'Patrick Hand', label: 'Patrick Hand' },
                { value: 'Paytone One', label: 'Paytone One' },
                { value: 'Permanent Marker', label: 'Permanent Marker' },
                { value: 'Pinyon Script', label: 'Pinyon Script' },
                { value: 'Playfair Display', label: 'Playfair Display' },
                { value: 'Poppins', label: 'Poppins' },
                { value: 'PT Serif', label: 'PT Serif' },
                { value: 'Quicksand', label: 'Quicksand' },
                { value: 'Sacramento', label: 'Sacramento' },
                { value: 'Source Sans Pro', label: 'Source Sans Pro' },
                { value: 'Ultra', label: 'Ultra' }
            ],
        };

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    firstUpdated() {
        this.initialMapStyleName = topicData.topic?.custom_map_style_name ?? 'standard';
        this.initialHaloColor = topicData.topic?.featured_color ?? '#35f0f3';
        this.imageToUpload = undefined;
    }

    onChange(e: CustomEvent<SettingsChangeEvent>) {
        const updatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;



        this.pendingUpdates[fieldToUpdate] = updatedValue;
    }

    onChangeHeaderLogoImage(e: CustomEvent) {
        this.imageToUpload = e.detail?.imageToUpload;
    }

    openUpgradeDialog(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: e.detail.featureType
                },
            })
        );
    }

    async hasChange(e: CustomEvent) {
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue
        }, this.amplitude_callsite, true, true);

        if ( e.detail.field === 'point_sort_order' && topicData.topic !== undefined) {
            // re order
            await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
        }

        this.requestUpdate();
        topicData.mapComponent?.requestUpdate();
        await topicData.mapComponent?.pointCardPaginationEl?.firstUpdated();

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }
    }

    protected render() {
        return html`
            <div id="container">
                <rename-map
                    .open="${this.open}"
                >
                </rename-map>
                <header-colors .open="${this.open}">
                </header-colors>
                <br>
                <h4 class="header-colors-label">
                    Font
                </h4>
                <setting-component
                    style="width:${this.fontField.widthPercentage ?? 100}%"
                    .setting_details=${this.fontField}
                    .is_hidden=${this.fontField.isHidden ?? false}
                    @on-change-value=${this.hasChange}
                    @open-upgrade-dialog=${this.openUpgradeDialog}
                >
                </setting-component>
                <header-logo
                    @on-change-value=${this.onChange}
                    @open-upgrade-dialog=${this.openUpgradeDialog}
                >
                </header-logo>
                <br>
                <map-orientation
                    .open="${this.open}"
                >
                </map-orientation>
                <map-skin> </map-skin>
                <br>
                <hide-header
                    .open="${this.open}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}
                >
                </hide-header>
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }
            #container {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                padding-top: 5px;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
            }

            .style__actions {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                padding: 0.5rem;
                gap: 0.5rem;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .modalHeader {
                text-align: center;
            }

            .header-colors-label {
                margin: 10px 0;
            }

            .helpHeader {
                color: var(--main-primary-color);
                text-align: center;
                padding-top: 0;
                padding-bottom: 0.25rem;
            }

            .helpSubHeader {
                margin-bottom: 0px;
                font-size: 0.75rem;
                color: var(--theme-input-color);
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }

}
